import { LoggerServiceImpl } from "@/services/logger/LoggerServiceImpl";
import type { Logger, LoggerOptions } from "@/types/Logger";

/**
 * Global logger service instance (singleton)
 */
let loggerServiceInstance: LoggerServiceImpl | null = null;

/**
 * Get the global logger service instance
 */
export function getLoggerService(): LoggerServiceImpl {
  loggerServiceInstance ??= new LoggerServiceImpl();
  return loggerServiceInstance;
}

/**
 * Refresh logger configuration from localStorage
 * Call this after setting DEBUG or LOG_LEVEL in localStorage
 *
 * @example
 * ```typescript
 * // In browser console:
 * localStorage.setItem('DEBUG', 'six3ro:p2p:*');
 * localStorage.setItem('LOG_LEVEL', 'debug');
 *
 * // Then refresh the logger configuration:
 * window.refreshLoggerConfig?.();
 * // or import and call directly:
 * import { refreshLoggerConfig } from '@/utils/logger';
 * refreshLoggerConfig();
 * ```
 */
export function refreshLoggerConfig(): void {
  getLoggerService().refreshFromStorage();
}

/**
 * Enable logs for a specific namespace pattern
 * @param pattern - Namespace pattern (e.g., 'six3ro:p2p:*')
 *
 * @example
 * ```typescript
 * // In browser console:
 * window.enableLogs('six3ro:p2p:*');
 * ```
 */
export function enableLogs(pattern: string): void {
  const current = globalThis.localStorage?.getItem('DEBUG') ?? '';
  const patterns = current ? current.split(',').map(p => p.trim()).filter(Boolean) : [];
  if (!patterns.includes(pattern)) {
    patterns.push(pattern);
    globalThis.localStorage?.setItem('DEBUG', patterns.join(','));
    refreshLoggerConfig();
    /* eslint-disable-next-line no-console -- Intentional feedback for console command */
    console.log(`[Logger] Enabled logs for: ${pattern}`);
  }
}

/**
 * Disable logs for a specific namespace pattern
 * @param pattern - Namespace pattern to disable
 *
 * @example
 * ```typescript
 * // In browser console:
 * window.disableLogs('six3ro:auth');
 * ```
 */
export function disableLogs(pattern: string): void {
  const current = globalThis.localStorage?.getItem('DEBUG') ?? '';
  const patterns = current.split(',').map(p => p.trim()).filter(p => p !== pattern && p.length > 0);
  globalThis.localStorage?.setItem('DEBUG', patterns.join(','));
  refreshLoggerConfig();
  /* eslint-disable-next-line no-console -- Intentional feedback for console command */
  console.log(`[Logger] Disabled logs for: ${pattern}`);
}

/**
 * Disable all logs
 *
 * @example
 * ```typescript
 * // In browser console:
 * window.disableAllLogs();
 * ```
 */
export function disableAllLogs(): void {
  globalThis.localStorage?.removeItem('DEBUG');
  getLoggerService().disableAll();
  /* eslint-disable-next-line no-console -- Intentional feedback for console command */
  console.log('[Logger] All logs disabled');
}

/**
 * Set the global log level
 * @param level - Log level: 'error', 'warn', 'info', 'debug', 'trace'
 *
 * @example
 * ```typescript
 * // In browser console:
 * window.setLogLevel('debug');
 * ```
 */
export function setLogLevel(level: string): void {
  const validLevels = ['error', 'warn', 'info', 'debug', 'trace'];
  if (!validLevels.includes(level)) {
    /* eslint-disable-next-line no-console -- Intentional feedback for console command */
    console.error(`[Logger] Invalid log level: ${level}. Valid levels: ${validLevels.join(', ')}`);
    return;
  }
  globalThis.localStorage?.setItem('LOG_LEVEL', level);
  refreshLoggerConfig();
  /* eslint-disable-next-line no-console -- Intentional feedback for console command */
  console.log(`[Logger] Log level set to: ${level}`);
}

/**
 * Show current logger configuration status
 *
 * @example
 * ```typescript
 * // In browser console:
 * window.showLoggerStatus();
 * ```
 */
export function showLoggerStatus(): void {
  const debugPatterns = globalThis.localStorage?.getItem('DEBUG') ?? '(not set - using env default)';
  const logLevel = globalThis.localStorage?.getItem('LOG_LEVEL') ?? '(not set - using env default)';

  /* eslint-disable no-console -- Intentional output for console command */
  console.log('%c[Logger Status]', 'color: #00aaff; font-weight: bold;');
  console.log(`  DEBUG patterns: ${debugPatterns}`);
  console.log(`  LOG_LEVEL: ${logLevel}`);
  console.log('');
  console.log('%cAvailable commands:', 'color: #888;');
  console.log('  window.enableLogs("six3ro:p2p:*")  - Enable namespace pattern');
  console.log('  window.disableLogs("six3ro:auth")  - Disable namespace pattern');
  console.log('  window.disableAllLogs()            - Disable all logs');
  console.log('  window.setLogLevel("debug")        - Set log level');
  console.log('  window.refreshLoggerConfig()       - Refresh from localStorage');
  /* eslint-enable no-console */
}

// Expose all commands to window for easy debugging
interface WindowWithLogger extends Window {
  refreshLoggerConfig?: () => void;
  enableLogs?: (pattern: string) => void;
  disableLogs?: (pattern: string) => void;
  disableAllLogs?: () => void;
  setLogLevel?: (level: string) => void;
  showLoggerStatus?: () => void;
}

const windowWithLogger = globalThis.window as WindowWithLogger;
windowWithLogger.refreshLoggerConfig = refreshLoggerConfig;
windowWithLogger.enableLogs = enableLogs;
windowWithLogger.disableLogs = disableLogs;
windowWithLogger.disableAllLogs = disableAllLogs;
windowWithLogger.setLogLevel = setLogLevel;
windowWithLogger.showLoggerStatus = showLoggerStatus;

/**
 * Factory function for creating namespaced loggers
 *
 * Inspired by @libp2p/logger - call this at the top of your module
 * to create a logger for that module.
 *
 * @param namespace - Logger namespace (e.g., 'six3ro:auth', 'six3ro:p2p:helia')
 * @returns A logger instance
 *
 * @example
 * ```typescript
 * // At the top of your service file
 * const logger = createLogger({ namespace: 'six3ro:auth' });
 *
 * export class AuthServiceImpl extends AuthService {
 *   async login(credentials: LoginDTO) {
 *     logger.info('Login attempt', { username: credentials.username });
 *
 *     try {
 *       const result = await this.api.post('/login', credentials);
 *       logger.debug('Login response', result);
 *       return result;
 *     } catch (error) {
 *       logger.error('Login failed', error);
 *       throw error;
 *     }
 *   }
 * }
 * ```
 */
export function createLogger(options: LoggerOptions): Logger {
  const service = getLoggerService();
  return service.createLogger(options);
}

/**
 * Convenience function to create a logger with just a namespace string
 *
 * @param namespace - Logger namespace
 * @returns A logger instance
 *
 * @example
 * ```typescript
 * const logger = logger('six3ro:p2p:helia');
 * logger.info('Helia node started');
 * ```
 */
export function logger(namespace: string): Logger {
  return createLogger({ namespace });
}

/**
 * Re-export types and enums for convenience
 */
export { LogLevel } from '@/types/Logger';
export type { Logger, LoggerOptions, LoggerConfig } from '@/types/Logger';
